// CD.Ajax - a utility wrapper for jQuery's $.ajax 
//
// Cd.Ajax().Url(fooUrl).Get().done(function (data) { ... });

import S from 's-js';
import TZDate from 'lib/TZDate';
import { DataCache } from './DataCache';
import { jsonizeSignals, SignalsAccessor } from './jsonizeSignals';
import { sumSignal } from './sumSignal';
import * as $ from 'jquery';
import * as Promise from 'bluebird';
import * as _ from 'underscore';

export interface IAjax {
    LastRequest: () => Promise<Request<any> | null>;
    LastResponse: () => Response<any> | null;
    Url<T>(address: string | (() => string), mappingOptions?: MappingOptions<T>) : Client<T, T>;
    Url<T, U>(address: string | (() => string), mappingOptions?: MappingOptions<T>) : Client<T, U>;
}

export interface Client<T, U> {
    Get() : Request<U>;
    Post(obj: T) : Request<U>;
    Put(obj: T) : Request<U>;
    Delete(obj?: T) : Request<U>;
    LastRequest: () => Promise<Request<U> | null>;
    LastResponse: () => Response<U> | null;
};

export interface Request<U> extends Promise<U> {
    Type : string,
    Url : string,
    Response: () => Response<U> | null,
    Date: Date
}

export interface Response<U> {
    Success: boolean,
    Status: number,
    Data: U,
    Request: Request<U>,
    Xhr: XMLHttpRequest,
    Date: Date | null,
    UsedCache: boolean
}

export interface MappingOptions<T> {
    useCache? : boolean,
    include?: SignalsAccessor<T>[]
}

export default Ajax;

function Ajax(dataCache : DataCache & { update: (timestamp : Date, json : any) => any }) : IAjax {
    var ajax = {
        LastRequest: sumSignal(Promise.resolve<any | null>(null)),
        LastResponse: S.data<Response<any> | null>(null),
        Url: Url
    };

    return ajax;

    function Url<T>(address : string | (() => string), mappingOptions : MappingOptions<T> = {}) {

        var url = {
            Get: Method("GET"),
            Post: Method('POST'),
            Put: Method("PUT"),
            Delete: Method('DELETE'),

            LastRequest: sumSignal(Promise.resolve<T | null>(null)),
            LastResponse: S.data<Response<T> | null>(null)
        };

        return url;

        function Method(type : string) {
            return function (obj? : any) {
                var response = S.data<Response<T> | null>(null),
                    request = new Promise(function (resolve, reject) {
                    // NOTE: FormData is undefined on IE9 or below
                    // NOTE: in modern browsers we could use .__proto__ property instead
                    // But .__proto__ is undefined in IE10
                    var isFormData = FormData.prototype.isPrototypeOf(obj),
                        data = 
                            type === "GET" || obj === undefined ? undefined :
                            isFormData                          ? obj :
                            mappingOptions.include              ? jsonizeSignals(obj, mappingOptions.include) :
                            JSON.stringify(obj),
                        useCache = mappingOptions.useCache && type !== "GET",
                        ajaxOptions : JQueryAjaxSettings = {
                            type: type,
                            url: unwrap(address),
                            data: data,
                            contentType: isFormData ? false : 'application/json; charset=utf-8',
                            // xhrFields.withCredentials tells the browser to send along cookies,
                            // even if this is a CORS request
                            xhrFields: {
                                withCredentials: true
                            },
                            processData: false
                        },
                        usedCache = false;
                    
                    if (useCache) {
                        ajaxOptions.accepts = { datacache: "application/x-pcc-data-cache" };
                        ajaxOptions.converters = { "json datacache": function (data : any) {
                            // TH 02/06/2017 -- Update: making the dataCache a parameter to Ajax instead of part of
                            // the mappingOptions to IAjax.Url.
                            // My main goal is to catch typos on the useCache option.
                            // NOTE: Currently, this version will allow you to specify useCache: true even if your response
                            // type on the ajax.Url call doesn't match the dataCache's update method.
                            // Fixing that would probably require adding a type param to the IAjax interface, which feels
                            // like overkill currently.
                            usedCache = true;
                            return dataCache.update(request.Date, data);
                        }};
                        ajaxOptions.dataType = 'datacache';
                    }

                    // The next line enables sending CORS requests on IE9
                    $.support.cors = true;

                    $.ajax(ajaxOptions)
                    .done(function (data : any, textStatus : string, xhr : XMLHttpRequest) {
                        var _response = {
                            Success: true,
                            Status: xhr.status,
                            Data: data,
                            Request: request,
                            Xhr: xhr,
                            Date: getDateHeader(xhr),
                            UsedCache: usedCache
                        };

                        S.freeze(function () {
                            resolve(data);
                            response(_response);
                            url.LastResponse(_response);
                            ajax.LastResponse(_response);
                        });
                    }).fail(function (xhr) {
                        var data = xhr.responseText;

                        // see if we can parse the response as JSON data
                        try {
                            data = JSON.parse(data);
                        } catch (ex) {
                        }

                        var _response = {
                            Success: false,
                            Status: xhr.status,
                            Data: data,
                            Request: request,
                            Xhr: xhr,
                            Date: getDateHeader(xhr),
                            UsedCache: usedCache
                        };

                        S.freeze(function () {
                            reject(data);
                            response(_response);
                            url.LastResponse(_response);
                            ajax.LastResponse(_response);
                        });
                    });
                }) as Request<T>;

                request.Type = type;
                request.Url = unwrap(address);
                request.Response = response;
                request.Date = new Date();

                S.freeze(function () {
                    url.LastRequest(_ => request);
                    ajax.LastRequest(_ => request);
                });

                return request;
            };
        }
    }
}

function unwrap(o : string | (() => string)) : string {
    return o instanceof Function ? S.sample(o) : o;
}

function getDateHeader(xhr : XMLHttpRequest) {
    var date = xhr.getResponseHeader("Date");
    return date ? new Date(date) : null;
}



// WEBPACK FOOTER //
// ./lib/Ajax.ts